import { Injectable } from '@angular/core';
import { assign, cloneDeep } from 'lodash-es';
import { FuseMockApiService, FuseMockApiUtils } from '@fuse/lib/mock-api';
import { boards as boardsData, cards as cardsData, labels as labelsData, lists as listsData, members as membersData } from 'app/mock-api/apps/scrumboard/data';

@Injectable({
    providedIn: 'root'
})
export class ScrumboardMockApi
{
    // Private
    private readonly _boards: any[] = boardsData;
    private _cards: any[] = cardsData;
    private readonly _labels: any[] = labelsData;
    private _lists: any[] = listsData;
    private readonly _members: any[] = membersData;

    /**
     * Constructor
     */
    constructor(
        private readonly _fuseMockApiService: FuseMockApiService
    ){
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Boards - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/scrumboard/boards')
            .reply(this._handleGetBoards.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Board - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/scrumboard/board')
            .reply(this._handleGetBoard.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ List - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/apps/scrumboard/board/list')
            .reply(this._handlePostList.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ List - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/list')
            .reply(this._handlePatchList.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Lists - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/lists')
            .reply(this._handlePatchLists.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ List - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/scrumboard/board/list')
            .reply(this._handleDeleteList.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Card - PUT
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPut('api/apps/scrumboard/board/card')
            .reply(this._handlePutCard.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Card - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/card')
            .reply(this._handlePatchCard.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Cards - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/cards')
            .reply(this._handlePatchCards.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Card - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/scrumboard/board/card')
            .reply(this._handleDeleteCard.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Card Positions - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/card/positions')
            .reply(this._handlePatchCardPositions.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Labels - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/scrumboard/board/labels')
            .reply(this._handleGetLabels.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Label - PUT
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPut('api/apps/scrumboard/board/label')
            .reply(this._handlePutLabel.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Label - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/scrumboard/board/label')
            .reply(this._handlePatchLabel.bind(this));

        // -----------------------------------------------------------------------------------------------------
        // @ Label - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/scrumboard/board/label')
            .reply(this._handleDeleteLabel.bind(this));
    }

    /**
     * Handle GET request for boards
     */
    private _handleGetBoards({ request }): [number, any[]] {
        let boards = cloneDeep(this._boards);
        boards = boards.map(board => ({
            ...board,
            members: board.members.map(boardMember => this._members.find(member => boardMember === member.id))
        }));
        return [200, boards];
    }

    /**
     * Handle GET request for a single board
     */
    private _handleGetBoard({ request }): [number, any] {
        const id = request.params.get('id');
        const board = this._boards.find(item => item.id === id);
        board.lists = this._lists.filter(item => item.boardId === id).sort((a, b) => a.position - b.position);
        let cards = this._cards.filter(item => item.boardId === id);
        cards = cards.map(card => ({
            ...card,
            labels: card.labels.map(cardLabelId => this._labels.find(label => label.id === cardLabelId))
        }));
        board.lists.forEach((list, index, array) => {
            array[index].cards = cards.filter(item => item.boardId === id && item.listId === list.id).sort((a, b) => a.position - b.position);
        });
        board.labels = this._labels.filter(item => item.boardId === id);
        return [200, cloneDeep(board)];
    }

    /**
     * Handle POST request to create a new list
     */
    private _handlePostList({ request }): [number, any] {
        const newList = cloneDeep(request.body.list);
        newList.id = FuseMockApiUtils.guid();
        this._lists.push(newList);
        return [200, newList];
    }

    /**
     * Handle PATCH request to update a list
     */
    private _handlePatchList({ request }): [number, any] {
        const list = cloneDeep(request.body.list);
        let updatedList = null;
        this._lists.forEach((item, index, lists) => {
            if (item.id === list.id) {
                lists[index] = assign({}, lists[index], list);
                updatedList = lists[index];
            }
        });
        return [200, updatedList];
    }

    /**
     * Handle PATCH request to update multiple lists
     */
    private _handlePatchLists({ request }): [number, any[]] {
        const lists = cloneDeep(request.body.lists);
        const updatedLists = [];
        lists.forEach((item) => {
            const index = this._lists.findIndex(list => item.id === list.id);
            this._lists[index] = assign({}, this._lists[index], item);
            updatedLists.push(item);
        });
        return [200, updatedLists];
    }

    /**
     * Handle DELETE request to delete a list
     */
    private _handleDeleteList({ request }): [number, boolean] {
        const id = request.params.get('id');
        const index = this._lists.findIndex(item => item.id === id);
        this._lists.splice(index, 1);
        this._cards = this._cards.filter(card => card.listId !== id);
        return [200, true];
    }

    /**
     * Handle PUT request to create a new card
     */
    private _handlePutCard({ request }): [number, any] {
        const newCard = cloneDeep(request.body.card);
        newCard.id = FuseMockApiUtils.guid();
        this._cards.push(newCard);
        return [200, newCard];
    }

    /**
     * Handle PATCH request to update a card
     */
    private _handlePatchCard({ request }): [number, any] {
        const id = request.body.id;
        const card = cloneDeep(request.body.card);
        let updatedCard = null;
        card.labels = card.labels.map(itemLabel => itemLabel.id);
        this._cards.forEach((item, index, cards) => {
            if (item.id === id) {
                cards[index] = assign({}, cards[index], card);
                updatedCard = cloneDeep(cards[index]);
            }
        });
        updatedCard.labels = updatedCard.labels.map(cardLabelId => this._labels.find(label => label.id === cardLabelId));
        return [200, updatedCard];
    }

    /**
     * Handle PATCH request to update multiple cards
     */
    private _handlePatchCards({ request }): [number, any[]] {
        const cards = cloneDeep(request.body.cards);
        const updatedCards = [];
        cards.forEach((item) => {
            const index = this._cards.findIndex(card => item.id === card.id);
            item.labels = item.labels.map(itemLabel => itemLabel.id);
            this._cards[index] = assign({}, this._cards[index], item);
            item.labels = item.labels.map(cardLabelId => this._labels.find(label => label.id === cardLabelId));
            updatedCards.push(item);
        });
        return [200, updatedCards];
    }

    /**
     * Handle DELETE request to delete a card
     */
    private _handleDeleteCard({ request }): [number, boolean] {
        const id = request.params.get('id');
        const index = this._cards.findIndex(item => item.id === id);
        this._cards.splice(index, 1);
        return [200, true];
    }

    /**
     * Handle PATCH request to update card positions
     */
    private _handlePatchCardPositions({ request }): [number, any[]] {
        const cards = request.body.cards;
        this._cards.forEach((card) => {
            card.position = cards.findIndex(item => item.id === card.id && item.listId === card.listId && item.boardId === card.boardId);
        });
        const updatedCards = cloneDeep(this._cards);
        return [200, updatedCards];
    }

    /**
     * Handle GET request for labels
     */
    private _handleGetLabels({ request }): [number, any[]] {
        const boardId = request.params.get('boardId');
        const labels = this._labels.filter(item => item.boardId === boardId);
        return [200, cloneDeep(labels)];
    }

    /**
     * Handle PUT request to create a new label
     */
    private _handlePutLabel({ request }): [number, any] {
        const newLabel = cloneDeep(request.body.label);
        newLabel.id = FuseMockApiUtils.guid();
        this._labels.unshift(newLabel);
        return [200, newLabel];
    }

    /**
     * Handle PATCH request to update a label
     */
    private _handlePatchLabel({ request }): [number, any] {
        const id = request.body.id;
        const label = cloneDeep(request.body.label);
        let updatedLabel = null;
        this._labels.forEach((item, index, labels) => {
            if (item.id === id) {
                labels[index] = assign({}, labels[index], label);
                updatedLabel = labels[index];
            }
        });
        return [200, updatedLabel];
    }

    /**
     * Handle DELETE request to delete a label
     */
    private _handleDeleteLabel({ request }): [number, boolean] {
        const id = request.params.get('id');
        const index = this._labels.findIndex(item => item.id === id);
        this._labels.splice(index, 1);
        const cardsWithLabel = this._cards.filter(card => card.labels.indexOf(id) > -1);
        cardsWithLabel.forEach((card) => {
            card.tags.splice(card.tags.indexOf(id), 1);
        });
        return [200, true];
    }
}
